import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  List,
  MenuItem,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ServicePeriodPicker from './ServicePeriodPicker';
import { formatInvoiceServicePeriodLabel, omitServicePeriodFromInvoiceTemplate } from '../utils/invoiceUtils';

/**
 * Reusable send workflow for preinvoices and invoices.
 */
export default function InvoiceSendPanel({
  title,
  description,
  sendButtonLabel,
  selectionDialogTitle,
  messageDialogTitle,
  defaultMessageTemplate,
  templatePlaceholders,
  getRecipients,
  sendDocuments,
  previewMessage,
  showServiceField = false,
  defaultService = '',
  showServicePeriodField = false,
  showDeliveryModeOption = false,
  defaultPaymentStatus = 'pending payment',
  paymentStatusOptions = ['Sent', 'pending payment', 'Payed']
}) {
  const [open, setOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [amountEur, setAmountEur] = useState('40');
  const [serviceDescription, setServiceDescription] = useState(defaultService);
  const [isTest, setIsTest] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [messageTemplate, setMessageTemplate] = useState(defaultMessageTemplate);
  const [servicePeriodStart, setServicePeriodStart] = useState('');
  const [servicePeriodEnd, setServicePeriodEnd] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(defaultPaymentStatus);

  const loadRecipients = async () => {
    try {
      const list = await getRecipients();
      setRecipients(list);
    } catch (err) {
      setError(err.message || 'Nepavyko įkelti klientų sąrašo.');
    }
  };

  const openModal = async () => {
    setError('');
    setRecipientSearch('');
    setSendEmail(true);
    setPaymentStatus(defaultPaymentStatus);
    setOpen(true);
    if (recipients.length === 0) {
      await loadRecipients();
    }
  };

  const closeModal = () => {
    if (sending) return;
    setOpen(false);
    setMessageOpen(false);
    setConfirmOpen(false);
  };

  const goToMessageStep = () => {
    setError('');
    if (
      showServicePeriodField
      && !formatInvoiceServicePeriodLabel(servicePeriodStart, servicePeriodEnd)
    ) {
      setMessageTemplate((current) => omitServicePeriodFromInvoiceTemplate(current));
    }
    setOpen(false);
    if (showDeliveryModeOption && !sendEmail) {
      setConfirmOpen(true);
      return;
    }
    setMessageOpen(true);
  };

  const goBackToSelectionStep = () => {
    if (sending) return;
    setMessageOpen(false);
    setOpen(true);
  };

  const toggleRecipient = (id) => {
    setSelectedRecipientIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const selectedRecipients = useMemo(() => {
    const byId = new Map(recipients.map((r) => [r.id, r]));
    return selectedRecipientIds.map((id) => byId.get(id)).filter(Boolean);
  }, [recipients, selectedRecipientIds]);

  const unselectedFilteredRecipients = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    const selectedIdSet = new Set(selectedRecipientIds);
    const unselected = recipients.filter((r) => !selectedIdSet.has(r.id));
    if (!q) return unselected;
    return unselected.filter((r) =>
      (r.fullName || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q)
    );
  }, [recipients, recipientSearch, selectedRecipientIds]);

  const parsedAmount = Number(amountEur) || 0;
  const canSubmit = parsedAmount > 0 && selectedRecipients.length > 0 && !sending;
  const canGoToMessage = parsedAmount > 0 && selectedRecipients.length > 0;

  const messagePreview = useMemo(() => {
    const firstRecipient = selectedRecipients[0];
    const periodLabel = showServicePeriodField
      ? formatInvoiceServicePeriodLabel(servicePeriodStart, servicePeriodEnd)
      : '';
    const templateForPreview = showServicePeriodField && !periodLabel
      ? omitServicePeriodFromInvoiceTemplate(messageTemplate)
      : messageTemplate;
    const previewVars = {
      vardas: firstRecipient?.fullName || 'Klientas',
      pdfUrl: '[PDF nuoroda bus sugeneruota siunčiant]',
      suma: parsedAmount > 0 ? `${parsedAmount.toFixed(2)} Eur` : '',
      saskaitosNumeris: '[sąskaitos numeris]',
      paslauguLaikotarpis: periodLabel,
      data: new Date().toISOString().slice(0, 7)
    };
    return previewMessage(templateForPreview, previewVars);
  }, [
    messageTemplate,
    selectedRecipients,
    parsedAmount,
    previewMessage,
    showServicePeriodField,
    servicePeriodStart,
    servicePeriodEnd
  ]);

  const confirmSummary = useMemo(() => {
    const parts = [
      `Patvirtinkite siuntimą: ${selectedRecipients.length} gavėjų`,
      `suma ${parsedAmount.toFixed(2)} Eur`
    ];
    if (showServicePeriodField && servicePeriodStart) {
      const period = servicePeriodEnd && servicePeriodEnd !== servicePeriodStart
        ? `${servicePeriodStart} – ${servicePeriodEnd}`
        : servicePeriodStart;
      parts.push(`paslaugų laikotarpis ${period}`);
    }
    parts.push(`test režimas: ${isTest ? 'Taip' : 'Ne'}`);
    parts.push(`statusas: ${paymentStatus}`);
    if (showDeliveryModeOption) {
      parts.push(sendEmail ? 'siųsti el. laišką: Taip' : 'tik išsaugoti (be el. laiško)');
    }
    return `${parts.join(', ')}.`;
  }, [
    selectedRecipients.length,
    parsedAmount,
    showServicePeriodField,
    servicePeriodStart,
    servicePeriodEnd,
    isTest,
    showDeliveryModeOption,
    sendEmail,
    paymentStatus
  ]);

  const handleSendConfirmed = async () => {
    setConfirmOpen(false);
    setSending(true);
    setError('');
    try {
      await sendDocuments({
        amountEur: parsedAmount,
        serviceDescription,
        recipients: selectedRecipients,
        isTest,
        messageTemplate,
        paymentStatus,
        ...(showServicePeriodField && {
          servicePeriodStart,
          servicePeriodEnd
        }),
        ...(showDeliveryModeOption && { sendEmail })
      });
      setSelectedRecipientIds([]);
      setRecipientSearch('');
      setMessageTemplate(defaultMessageTemplate);
      setServicePeriodStart('');
      setServicePeriodEnd('');
      setMessageOpen(false);
      setOpen(false);
    } catch (err) {
      setError(err.message || 'Nepavyko išsiųsti sąskaitų.');
    } finally {
      setSending(false);
    }
  };

  const renderRecipientList = () => (
    <List
      dense
      subheader={<li />}
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflowY: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 0
      }}
    >
      {selectedRecipients.length > 0 && (
        <li>
          <ul style={{ padding: 0 }}>
            <ListSubheader
              sx={{
                bgcolor: 'primary.50',
                color: 'primary.main',
                fontWeight: 700,
                lineHeight: '32px',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              Pasirinkti ({selectedRecipients.length})
            </ListSubheader>
            {selectedRecipients.map((r) => (
              <ListItem
                key={`sel-${r.id}`}
                disablePadding
                sx={{ bgcolor: 'action.selected' }}
                secondaryAction={(
                  <Checkbox
                    edge="end"
                    checked
                    tabIndex={-1}
                    disableRipple
                    onChange={() => toggleRecipient(r.id)}
                  />
                )}
              >
                <ListItemButton onClick={() => toggleRecipient(r.id)} sx={{ pl: 1.5, pr: 6 }}>
                  <ListItemText primary={r.fullName || 'Be vardo'} secondary={r.email} />
                </ListItemButton>
              </ListItem>
            ))}
          </ul>
        </li>
      )}
      <li>
        <ul style={{ padding: 0 }}>
          <ListSubheader
            sx={{
              bgcolor: 'grey.100',
              color: 'text.secondary',
              fontWeight: 600,
              lineHeight: '32px',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            {recipientSearch.trim() ? 'Paieškos rezultatai' : 'Visi klientai'}
          </ListSubheader>
          {unselectedFilteredRecipients.map((r) => (
            <ListItem
              key={r.id}
              disablePadding
              secondaryAction={(
                <Checkbox
                  edge="end"
                  checked={false}
                  tabIndex={-1}
                  disableRipple
                  onChange={() => toggleRecipient(r.id)}
                />
              )}
            >
              <ListItemButton onClick={() => toggleRecipient(r.id)} sx={{ pl: 1.5, pr: 6 }}>
                <ListItemText primary={r.fullName || 'Be vardo'} secondary={r.email} />
              </ListItemButton>
            </ListItem>
          ))}
          {unselectedFilteredRecipients.length === 0 && (
            <ListItem>
              <ListItemText
                primary={
                  recipientSearch.trim()
                    ? 'Nieko nerasta pagal įvestą paiešką.'
                    : 'Daugiau klientų nėra.'
                }
              />
            </ListItem>
          )}
        </ul>
      </li>
    </List>
  );

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {description}
        </Typography>
        <Button variant="contained" onClick={openModal}>
          {sendButtonLabel}
        </Button>
      </Paper>

      <Dialog
        open={open}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            height: 'calc(100vh - 32px)',
            maxHeight: 'calc(100vh - 32px)',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>{selectionDialogTitle}</DialogTitle>
        <DialogContent sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, mt: 0.5 }}>
            <TextField
              type="number"
              label="Suma (Eur)"
              value={amountEur}
              onChange={(e) => setAmountEur(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel id="payment-status-label">Mokėjimo statusas</InputLabel>
              <Select
                labelId="payment-status-label"
                label="Mokėjimo statusas"
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
              >
                {paymentStatusOptions.map((st) => (
                  <MenuItem key={st} value={st}>{st}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {showServiceField && (
              <TextField
                label="Paslauga"
                value={serviceDescription}
                onChange={(e) => setServiceDescription(e.target.value)}
                fullWidth
              />
            )}
            {showServicePeriodField && (
              <ServicePeriodPicker
                startDate={servicePeriodStart}
                endDate={servicePeriodEnd}
                onChange={({ startDate, endDate }) => {
                  setServicePeriodStart(startDate);
                  setServicePeriodEnd(endDate);
                }}
              />
            )}
            {showDeliveryModeOption && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  Pristatymo būdas
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={sendEmail ? 'email' : 'store'}
                  onChange={(_, value) => {
                    if (value) setSendEmail(value === 'email');
                  }}
                >
                  <ToggleButton value="email">
                    <EmailOutlinedIcon sx={{ mr: 0.75, fontSize: 18 }} />
                    Siųsti el. paštu
                  </ToggleButton>
                  <ToggleButton value="store">
                    <SaveOutlinedIcon sx={{ mr: 0.75, fontSize: 18 }} />
                    Tik išsaugoti
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />}
                label="Test (nemažina sąskaitų sekos)"
              />
            </FormGroup>
            <Divider />
            <Typography variant="subtitle2">Klientų sąrašas</Typography>
            <TextField
              size="small"
              label="Ieškoti pagal vardą arba el. paštą"
              value={recipientSearch}
              onChange={(e) => setRecipientSearch(e.target.value)}
              fullWidth
            />
            <Box sx={{ flex: '1 1 auto', minHeight: 160, display: 'flex', flexDirection: 'column' }}>
              {renderRecipientList()}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={closeModal} disabled={sending}>Atšaukti</Button>
          <Button variant="contained" disabled={!canGoToMessage} onClick={goToMessageStep}>
            {showDeliveryModeOption && !sendEmail ? 'Toliau' : 'Kitas'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={messageOpen}
        onClose={closeModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: 'calc(100vh - 32px)',
            maxHeight: 'calc(100vh - 32px)',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>{messageDialogTitle}</DialogTitle>
        <DialogContent sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Žinutę galite redaguoti laisvai. Žemiau išvardinti vietos žymekliai bus automatiškai pakeisti kiekvienam gavėjui prieš siunčiant.
            </Typography>
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Galimi žymekliai:
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
                {templatePlaceholders.map((p) => (
                  <li key={p.key}>
                    <Typography variant="caption">
                      <code>{p.key}</code> — {p.description}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
            {showServicePeriodField && servicePeriodStart && servicePeriodEnd && (
              <Box sx={{ p: 1.5, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                  Paslaugų teikimo laikotarpis
                </Typography>
                <Typography variant="body2">
                  {formatInvoiceServicePeriodLabel(servicePeriodStart, servicePeriodEnd)}
                </Typography>
              </Box>
            )}
            <TextField
              label="Žinutės tekstas"
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              multiline
              minRows={10}
              fullWidth
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Peržiūra {selectedRecipients[0] ? `(pirmas gavėjas: ${selectedRecipients[0].fullName || selectedRecipients[0].email})` : ''}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: 'background.default',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  maxHeight: 220,
                  overflow: 'auto'
                }}
              >
                {messagePreview}
              </Paper>
            </Box>
            <Button
              size="small"
              onClick={() => {
                let next = defaultMessageTemplate;
                if (
                  showServicePeriodField
                  && !formatInvoiceServicePeriodLabel(servicePeriodStart, servicePeriodEnd)
                ) {
                  next = omitServicePeriodFromInvoiceTemplate(next);
                }
                setMessageTemplate(next);
              }}
              disabled={messageTemplate === defaultMessageTemplate || sending}
              sx={{ alignSelf: 'flex-start' }}
            >
              Atstatyti standartinį tekstą
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={goBackToSelectionStep} disabled={sending}>Atgal</Button>
          <Button variant="contained" disabled={!canSubmit || !messageTemplate.trim()} onClick={() => setConfirmOpen(true)}>
            Siųsti
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => {
        setConfirmOpen(false);
        if (!messageOpen) setOpen(true);
      }}>
        <DialogTitle>Patvirtinimas</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmSummary}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setConfirmOpen(false);
            if (!messageOpen) setOpen(true);
          }} disabled={sending}>Atšaukti</Button>
          <Button variant="contained" onClick={handleSendConfirmed} disabled={!canSubmit}>
            {showDeliveryModeOption && !sendEmail ? 'Išsaugoti' : 'Patvirtinti ir siųsti'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
